import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import ProviderDepositSettings from '../components/ProviderDepositSettings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  User, FileText, CreditCard, Building2, Camera, Upload, Check, X, 
  AlertCircle, Loader2, Shield, Car, IdCard, Clock, Eye, Phone, MapPin, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeSettings() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [directDeposit, setDirectDeposit] = useState([]);
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    emergency_contact: '',
    emergency_phone: ''
  });
  
  const [depositForm, setDepositForm] = useState({
    bank_name: '',
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    account_type: 'checking'
  });
  
  const [documentUpload, setDocumentUpload] = useState({
    type: 'drivers_license',
    file: null,
    preview: null,
    uploading: false
  });

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employee/profile-settings`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      setProfileData(response.data.profile);
      setDocuments(response.data.documents || []);
      setDirectDeposit(response.data.direct_deposit || []);
      
      // Populate profile form
      setProfileForm({
        name: response.data.profile?.name || '',
        phone: response.data.profile?.phone || '',
        email: response.data.profile?.email || '',
        address: response.data.profile?.address || '',
        emergency_contact: response.data.profile?.emergency_contact || '',
        emergency_phone: response.data.profile?.emergency_phone || ''
      });
    } catch (error) {
      console.error('Failed to fetch employee settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!user || !['mechanic', 'roadside_tech', 'admin', 'owner'].includes(user.user_type)) {
      toast.error('Employee access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/employee/profile-settings`, profileForm, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDirectDepositSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/employee/direct-deposit`, depositForm, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Direct deposit info saved');
      setDepositForm({
        bank_name: '',
        account_holder_name: '',
        routing_number: '',
        account_number: '',
        account_type: 'checking'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save direct deposit');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, or PDF');
      return;
    }
    
    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB');
      return;
    }
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentUpload(prev => ({ ...prev, file, preview: reader.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setDocumentUpload(prev => ({ ...prev, file, preview: null }));
    }
  };

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      // Create video element to capture
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      
      // Wait a moment for camera to stabilize
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Capture frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      
      // Stop stream
      stream.getTracks().forEach(track => track.stop());
      
      // Convert to blob
      canvas.toBlob((blob) => {
        const file = new File([blob], 'camera_capture.jpg', { type: 'image/jpeg' });
        setDocumentUpload(prev => ({ 
          ...prev, 
          file, 
          preview: canvas.toDataURL('image/jpeg') 
        }));
      }, 'image/jpeg', 0.9);
      
      toast.success('Photo captured!');
    } catch (error) {
      toast.error('Camera access denied or unavailable');
    }
  };

  const handleDocumentUpload = async () => {
    if (!documentUpload.file) {
      toast.error('Please select a file first');
      return;
    }
    
    setDocumentUpload(prev => ({ ...prev, uploading: true }));
    
    try {
      // Upload to object storage first
      const formData = new FormData();
      formData.append('file', documentUpload.file);
      
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      const fileUrl = uploadResponse.data.url;
      
      // Save document record
      await axios.post(`${API}/employee/documents`, {
        document_type: documentUpload.type,
        file_url: fileUrl,
        file_name: documentUpload.file.name,
        file_type: documentUpload.file.type
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success('Document uploaded successfully!');
      setDocumentUpload({ type: 'drivers_license', file: null, preview: null, uploading: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
      setDocumentUpload(prev => ({ ...prev, uploading: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="employee-settings">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <User className="w-6 h-6 text-blue-600" />
            Employee Profile Settings
          </h1>
          <p className="text-gray-600">Manage your profile, documents, and payment information</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-white w-full justify-start flex-wrap">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Documents
              {documents.filter(d => d.status === 'pending').length > 0 && (
                <Badge variant="outline" className="ml-1 bg-yellow-100">
                  {documents.filter(d => d.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="payout-methods" className="flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Payout Methods
            </TabsTrigger>
            <TabsTrigger value="direct-deposit" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Bank Account
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update your personal details and emergency contact</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={profileForm.address}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Street, City, State ZIP"
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="pt-4 border-t">
                    <h3 className="font-medium text-gray-900 mb-3">Emergency Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="emergency_contact">Contact Name</Label>
                        <Input
                          id="emergency_contact"
                          value={profileForm.emergency_contact}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, emergency_contact: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="emergency_phone">Contact Phone</Label>
                        <Input
                          id="emergency_phone"
                          type="tel"
                          value={profileForm.emergency_phone}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, emergency_phone: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button type="submit" disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="space-y-6">
              {/* Upload New Document */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Upload Document
                  </CardTitle>
                  <CardDescription>
                    Upload your driver's license, state ID, or other required documents.
                    Supports JPEG, PNG, and PDF files up to 10MB.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Document Type</Label>
                      <Select 
                        value={documentUpload.type} 
                        onValueChange={(val) => setDocumentUpload(prev => ({ ...prev, type: val }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="drivers_license">Driver's License</SelectItem>
                          <SelectItem value="state_id">State ID</SelectItem>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="vehicle_registration">Vehicle Registration</SelectItem>
                          <SelectItem value="insurance">Insurance Card</SelectItem>
                          <SelectItem value="certification">Certification</SelectItem>
                          <SelectItem value="other">Other Document</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Camera Capture */}
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="h-24 flex flex-col items-center justify-center gap-2"
                        onClick={handleCameraCapture}
                      >
                        <Camera className="w-8 h-8 text-blue-600" />
                        <span className="text-sm">Take Photo</span>
                      </Button>
                      
                      {/* File Upload */}
                      <label className="h-24 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <Upload className="w-8 h-8 text-gray-400" />
                        <span className="text-sm text-gray-600">Browse Files</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      
                      {/* Preview */}
                      {documentUpload.file && (
                        <div className="h-24 border rounded-lg overflow-hidden relative">
                          {documentUpload.preview ? (
                            <img src={documentUpload.preview} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <FileText className="w-8 h-8 text-gray-400" />
                              <span className="text-xs text-gray-500 ml-2">PDF</span>
                            </div>
                          )}
                          <button
                            onClick={() => setDocumentUpload(prev => ({ ...prev, file: null, preview: null }))}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {documentUpload.file && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">{documentUpload.file.name}</span>
                        <Button 
                          onClick={handleDocumentUpload} 
                          disabled={documentUpload.uploading}
                        >
                          {documentUpload.uploading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Upload
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Uploaded Documents */}
              <Card>
                <CardHeader>
                  <CardTitle>Uploaded Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  {documents.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No documents uploaded yet</p>
                  ) : (
                    <div className="space-y-3">
                      {documents.map((doc) => (
                        <div 
                          key={doc.document_id} 
                          className={`p-4 rounded-lg border flex items-center justify-between ${
                            doc.status === 'verified' ? 'bg-green-50 border-green-200' :
                            doc.status === 'rejected' ? 'bg-red-50 border-red-200' :
                            'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              doc.status === 'verified' ? 'bg-green-100' :
                              doc.status === 'rejected' ? 'bg-red-100' :
                              'bg-yellow-100'
                            }`}>
                              {doc.document_type === 'drivers_license' ? (
                                <Car className="w-5 h-5" />
                              ) : (
                                <IdCard className="w-5 h-5" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium capitalize">
                                {doc.document_type.replace(/_/g, ' ')}
                              </p>
                              <p className="text-xs text-gray-500">
                                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              doc.status === 'verified' ? 'bg-green-600' :
                              doc.status === 'rejected' ? 'bg-red-600' :
                              'bg-yellow-600'
                            }>
                              {doc.status === 'verified' && <Check className="w-3 h-3 mr-1" />}
                              {doc.status === 'rejected' && <X className="w-3 h-3 mr-1" />}
                              {doc.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                              {doc.status}
                            </Badge>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle className="capitalize">
                                    {doc.document_type.replace(/_/g, ' ')}
                                  </DialogTitle>
                                </DialogHeader>
                                {doc.file_type?.includes('pdf') ? (
                                  <iframe
                                    src={doc.file_url}
                                    className="w-full h-96 border rounded"
                                    title="Document Preview"
                                  />
                                ) : (
                                  <img
                                    src={doc.file_url}
                                    alt="Document"
                                    className="w-full rounded"
                                  />
                                )}
                                {doc.rejection_reason && (
                                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                                    <p className="text-sm text-red-700">
                                      <strong>Rejection reason:</strong> {doc.rejection_reason}
                                    </p>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payout Methods Tab - Venmo, Chime, Cash App, Zelle, Netspend */}
          <TabsContent value="payout-methods">
            <Card>
              <CardContent className="pt-6">
                <ProviderDepositSettings />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Direct Deposit Tab */}
          <TabsContent value="direct-deposit">
            <div className="space-y-6">
              {/* Add New Direct Deposit */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Setup Direct Deposit
                  </CardTitle>
                  <CardDescription>
                    Add your bank account for payouts. Your information is securely stored.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleDirectDepositSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="bank_name">Bank Name</Label>
                        <Input
                          id="bank_name"
                          value={depositForm.bank_name}
                          onChange={(e) => setDepositForm(prev => ({ ...prev, bank_name: e.target.value }))}
                          placeholder="Chase, Bank of America, etc."
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="account_holder_name">Account Holder Name</Label>
                        <Input
                          id="account_holder_name"
                          value={depositForm.account_holder_name}
                          onChange={(e) => setDepositForm(prev => ({ ...prev, account_holder_name: e.target.value }))}
                          placeholder="As shown on account"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="routing_number">Routing Number</Label>
                        <Input
                          id="routing_number"
                          value={depositForm.routing_number}
                          onChange={(e) => setDepositForm(prev => ({ ...prev, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                          placeholder="9-digit routing number"
                          className="mt-1"
                          required
                          maxLength={9}
                        />
                      </div>
                      <div>
                        <Label htmlFor="account_number">Account Number</Label>
                        <Input
                          id="account_number"
                          value={depositForm.account_number}
                          onChange={(e) => setDepositForm(prev => ({ ...prev, account_number: e.target.value.replace(/\D/g, '') }))}
                          placeholder="Account number"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Account Type</Label>
                      <Select 
                        value={depositForm.account_type} 
                        onValueChange={(val) => setDepositForm(prev => ({ ...prev, account_type: val }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking Account</SelectItem>
                          <SelectItem value="savings">Savings Account</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-700">
                        Your banking information is encrypted and securely stored. Only the last 4 digits of your account number will be displayed.
                      </p>
                    </div>
                    
                    <Button type="submit" disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Save Direct Deposit Info
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Existing Direct Deposit Accounts */}
              {directDeposit.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Your Bank Accounts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {directDeposit.map((deposit) => (
                        <div 
                          key={deposit.deposit_id}
                          className={`p-4 rounded-lg border flex items-center justify-between ${
                            deposit.status === 'active' ? 'bg-green-50 border-green-200' :
                            deposit.status === 'rejected' ? 'bg-red-50 border-red-200' :
                            'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Building2 className="w-8 h-8 text-gray-600" />
                            <div>
                              <p className="font-medium">{deposit.bank_name}</p>
                              <p className="text-sm text-gray-600">
                                {deposit.account_type} ****{deposit.account_number_last_four}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {deposit.is_primary && (
                              <Badge variant="outline" className="bg-blue-100">Primary</Badge>
                            )}
                            <Badge className={
                              deposit.status === 'active' ? 'bg-green-600' :
                              deposit.status === 'rejected' ? 'bg-red-600' :
                              'bg-yellow-600'
                            }>
                              {deposit.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
